import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MailX, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { onboardingApi, ApiError } from "@/lib/api";

// Lands here when the user clicks the link in their verification email.
// Reads `?token=` from the URL, calls the backend to confirm the token,
// hydrates the store with `domain`/`domain_exists`, and navigates to
// the domain-branch step.
export function StepVerifyToken() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { reload } = useAuth();
  const setStep = useOnboardingStore((s) => s.setStep);
  const setOnboarding = useOnboardingStore((s) => s.setOnboarding);
  const setVerified = useOnboardingStore((s) => s.setVerified);

  const [state, setState] = useState<
    | { kind: "pending" }
    | { kind: "error"; code: string; message: string }
    | { kind: "success" }
  >({ kind: "pending" });

  // Guard against StrictMode double-invocation.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    if (!token) {
      setState({
        kind: "error",
        code: "missing_token",
        message: "Missing verification token.",
      });
      return;
    }

    (async () => {
      try {
        const res = await onboardingApi.verifyEmail(token);
        setOnboarding(res.onboarding_id, res.email);
        setVerified(res.domain, res.domain_exists);
        try { await reload(); } catch { /* non-fatal */ }
        setState({ kind: "success" });
        // Park the wizard in `login` so when the user lands on /onboarding
        // after sign-in, the resume logic doesn't bounce them backward.
        setStep("login");
        toast.success("Email verified", {
          description: "Sign in with the password you set during registration.",
        });
        // Hand off to the dedicated sign-in page. From there, the auth-gated
        // wizard takes over — no more anonymous onboarding-id capability.
        setTimeout(() => navigate("/onboarding/login", { replace: true }), 600);
      } catch (err) {
        const ae = err as ApiError;
        const code = ae.code || "unknown_error";
        let msg = ae.message || "Verification failed";
        if (code === "invalid_token") msg = "This verification link is invalid.";
        if (code === "token_expired") msg = "This verification link has expired.";
        console.error("verifyEmail", ae);
        setState({ kind: "error", code, message: msg });
      }
    })();
  }, [token, reload, setOnboarding, setVerified, setStep, navigate]);

  const onboardingId = useOnboardingStore((s) => s.onboardingId);

  const requestNew = async () => {
    if (!onboardingId) {
      toast.error("Please restart onboarding");
      return;
    }
    try {
      await onboardingApi.resendVerification(onboardingId);
      toast.success("A new link was sent to your inbox");
    } catch (err) {
      const ae = err as ApiError;
      console.error("resendVerification", ae);
      toast.error("Couldn't resend", { description: ae.message });
    }
  };

  if (state.kind === "pending") {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
            <div>
              <CardTitle>Verifying your email</CardTitle>
              <CardDescription>This will just take a moment.</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (state.kind === "success") {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-signal-ok/15 p-2 text-signal-ok">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>Email verified</CardTitle>
              <CardDescription>Redirecting you to the next step…</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-signal-failed/15 p-2 text-signal-failed">
            <MailX className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Verification failed</CardTitle>
            <CardDescription>{state.message}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Button onClick={requestNew} size="lg">
          <RefreshCw className="h-4 w-4" />
          Request new link
        </Button>
      </CardContent>
    </Card>
  );
}
